import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Sms,
  Schedule,
  Group,
  NotificationImportant,
  CheckCircle,
  Phone,
  Security,
  Timeline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LandingLayout from './LandingLayout';

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();

  return (
    <LandingLayout>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Hero Section */}
        <Box textAlign="center" sx={{ mb: 8 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
            How Casante - Checks Works
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
            A simple, reliable daily wellness check system that gives you peace of mind about your loved ones
          </Typography>
        </Box>

        {/* Overview Section */}
        <Paper elevation={2} sx={{ p: 4, mb: 6, backgroundColor: '#F1F8E9' }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
            Simple Daily Wellness Checks
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
            Casante - Checks is designed to ensure that seniors or individuals living alone can confirm their 
            well-being each day, providing peace of mind to their caregivers. The system is incredibly simple - 
            your loved one just needs to respond to a daily text message.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, fontStyle: 'italic' }}>
            <strong>Important:</strong> This is a daily wellness check service, not an emergency response system. 
            It's designed to provide reassurance that your loved one is "fine" each day.
          </Typography>
        </Paper>

        {/* How It Works Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 6, color: '#2E7D32' }}>
            Simple 4-Step Process
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Schedule sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    1. Schedule Daily Check-In
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set up a convenient time for your loved one to receive their daily wellness check message
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Sms sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    2. Daily SMS Sent
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Each day at the scheduled time, an SMS is sent asking "Are you okay today?"
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <CheckCircle sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    3. Simple Response
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your loved one replies with "1", "Yes", or "I'm OK" to confirm they're doing well
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <NotificationImportant sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    4. Alerts if Needed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    If no response is received, caregivers are immediately notified to check in
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Key Features */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 4, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Group sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Care Circles
                </Typography>
              </Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Up to 8 caregivers per loved one" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Email and SMS invitation system" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Individual notification preferences" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Caregiver acknowledgment system" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 4, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Timeline sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Smart Scheduling
                </Typography>
              </Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Flexible check-in times and days" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Vacation mode for travel periods" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Timezone handling and conflicts" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Automatic pause/resume functionality" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 4, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Phone sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  No App Required
                </Typography>
              </Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Works with any phone with SMS" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="No smartphone or app needed" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Simple text message responses" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="International phone number support" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 4, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Reliable & Secure
                </Typography>
              </Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Retry logic for missed check-ins" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Secure user authentication" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Professional SMS service (Plivo)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </ListItemIcon>
                  <ListItemText primary="Comprehensive dashboard and reporting" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6 }} />

        {/* Call to Action */}
        <Box textAlign="center" sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Give yourself peace of mind with our simple daily wellness check system
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              backgroundColor: '#2E7D32',
              '&:hover': {
                backgroundColor: '#1B5E20'
              },
              px: 4,
              py: 1.5
            }}
          >
            Get Started
          </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                borderColor: '#2E7D32',
                color: '#2E7D32',
                '&:hover': {
                  borderColor: '#1B5E20',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)'
                },
                px: 4,
                py: 1.5
              }}
            >
              View Pricing
            </Button>
          </Box>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default HowItWorks;
